import {formatErrorMessage} from "../connection/ProtocolConstants"
import {browserShortName} from "../../utils/browserInfo"
import type {ISender} from "../connection/Connection"

const MAX_ERRORS_PER_WINDOW = 10
const RATE_LIMIT_WINDOW = 30_000
const MAX_BUFFER_SIZE = 20
const MAX_MESSAGE_LENGTH = 500
const MAX_STACK_LENGTH = 1000

interface ErrorEntry {
    t: string           // тип: 'unhandled', 'rejection', 'sdk'
    m: string           // сообщение ошибки
    s?: string          // stack trace
    ts: number          // timestamp
    ua: string          // браузер
    v: string           // версия билда
    n?: number          // кол-во повторов при дедупликации
    ctx?: Record<string, string>  // контекст: состояние SM, participant ID
}

class ErrorReporter {
    private sender: ISender | undefined
    private isConnected: (() => boolean) | undefined
    private buffer: ErrorEntry[] = []
    private timestamps: number[] = []
    private dedupMap = new Map<string, { entry: ErrorEntry, index: number }>()

    private onError: ((event: ErrorEvent) => void) | undefined
    private onRejection: ((event: PromiseRejectionEvent) => void) | undefined

    installGlobalHandlers(): void {
        this.onError = (event: ErrorEvent) => {
            this.report('unhandled', event.message, event.error)
        }
        this.onRejection = (event: PromiseRejectionEvent) => {
            const msg = event.reason instanceof Error ? event.reason.message : String(event.reason)
            const err = event.reason instanceof Error ? event.reason : undefined
            this.report('rejection', msg, err)
        }
        window.addEventListener('error', this.onError)
        window.addEventListener('unhandledrejection', this.onRejection)
    }

    install(sender: ISender, isConnected: () => boolean): void {
        this.sender = sender
        this.isConnected = isConnected
        this.flush()
    }

    uninstall(): void {
        this.sender = undefined
        this.isConnected = undefined
    }

    reportError(message: string, error?: unknown, ctx?: Record<string, string>): void {
        this.report('sdk', message, error instanceof Error ? error : undefined, ctx)
    }

    private report(type: string, message: string, error?: Error, ctx?: Record<string, string>): void {
        if (!this.checkRateLimit()) return

        const msg = message.slice(0, MAX_MESSAGE_LENGTH)
        const dedupKey = type + ':' + msg

        const existing = this.dedupMap.get(dedupKey)
        if (existing) {
            existing.entry.n = (existing.entry.n || 1) + 1
            return
        }

        const entry: ErrorEntry = {
            t: type,
            m: msg,
            ts: Date.now(),
            ua: browserShortName,
            v: (process.env.BUILD_VERSION as string) || 'dev',
        }

        if (error?.stack) {
            entry.s = error.stack.slice(0, MAX_STACK_LENGTH)
        }
        if (ctx) {
            entry.ctx = ctx
        }

        this.dedupMap.set(dedupKey, { entry, index: this.buffer.length })
        this.buffer.push(entry)
        if (this.buffer.length > MAX_BUFFER_SIZE) {
            const removed = this.buffer.shift()!
            const removedKey = removed.t + ':' + removed.m
            this.dedupMap.delete(removedKey)
            for (const [, val] of this.dedupMap) {
                val.index--
            }
        }

        this.flush()
    }

    private flush(): void {
        if (!this.sender || !this.isConnected?.()) return

        while (this.buffer.length > 0) {
            const entry = this.buffer.shift()!
            const dedupKey = entry.t + ':' + entry.m
            this.dedupMap.delete(dedupKey)
            this.sender.send(formatErrorMessage(entry))
        }
    }

    private checkRateLimit(): boolean {
        const now = Date.now()
        while (this.timestamps.length > 0 && now - this.timestamps[0] > RATE_LIMIT_WINDOW) {
            this.timestamps.shift()
        }
        if (this.timestamps.length >= MAX_ERRORS_PER_WINDOW) return false
        this.timestamps.push(now)
        return true
    }
}

export const errorReporter = new ErrorReporter()
